'use client'

import * as React from 'react'
import { useForm } from 'react-hook-form'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'

import ErrorIcon from '@mui/icons-material/Error'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  styled,
  Typography,
} from '@mui/material'
import { signIn, useSession } from 'next-auth/react'

import { ControlledInput } from '@/components/FormElements/ControlledInput'
import { useUser } from '@/hooks/sap/user/useUser'
import { useTrackEvent } from '@/hooks/useTrackEvent'
import { useTranslations } from '@/hooks/useTranslations'
import { AsmSessionContext } from '@/providers/AsmSessionContextProvider'

interface ValidationError {
  message: string | undefined
  status: number
}

type FormData = {
  email: string
  password: string
}

interface Props {
  callbackUrl?: string
  employeeSignIn: boolean
}

const StyledLabel = styled('label')(({ theme }) => ({
  display: 'inline-block',
  marginBottom: theme.spacing(1),
  color: theme.palette.secondary.main,
}))

export const LoginForm: React.FC<Props> = ({ callbackUrl, employeeSignIn }) => {
  const { handleSubmit, control } = useForm<FormData>()
  const [showPassword, setShowPassword] = React.useState(false)
  const [validationError, setValidationError] = React.useState<ValidationError | null>(null)
  const [loggingIn, setLoggingIn] = React.useState(false)
  const { translate } = useTranslations()
  const router = useRouter()
  const pathname = usePathname()
  const toggleShowPassword = () => setShowPassword(!showPassword)
  const { trackLogin } = useTrackEvent()
  const { data: session, status: authStatus } = useSession()
  const { isFetched: userDataFetched } = useUser()
  const { clearImpersonation } = React.useContext(AsmSessionContext)

  const signInFlow = employeeSignIn ? 'asmCredentialsFlow' : 'userCredentialsFlow'
  const hasLoggedLoginEventRef = React.useRef(false)

  // Wait for the SAP user profile fetch to settle so userData (customerId, roles, orgUnit, ...)
  // is filled in on the datalayer by the time trackLogin builds the GA4 context.
  React.useEffect(() => {
    if (
      authStatus === 'authenticated' &&
      session?.id != null &&
      loggingIn &&
      userDataFetched &&
      !hasLoggedLoginEventRef.current
    ) {
      hasLoggedLoginEventRef.current = true
      trackLogin()
    }
  }, [trackLogin, session, authStatus, loggingIn, userDataFetched])

  const handleSignInResponse = (res: Awaited<ReturnType<typeof signIn>>) => {
    if (!res) {
      return
    }

    const { error, status, code } = res

    if (code === 'user_migrated_no_password') {
      router.push('/forgottenPassword')
      return
    }

    if (error) {
      setValidationError({ message: error, status: status ?? 0 })
      return
    }

    if (status !== 200 && !res.ok) {
      return
    }

    setLoggingIn(true)

    // A fresh employee login must start from a clean impersonation state
    if (employeeSignIn) {
      clearImpersonation()
    }

    router.push(callbackUrl && callbackUrl !== pathname ? callbackUrl : '/')
  }

  const onSubmit = async (data: FormData) => {
    setLoggingIn(true)

    const res = await signIn(signInFlow, {
      redirect: false,
      username: data.email,
      password: data.password,
    })

    setLoggingIn(false)
    handleSignInResponse(res)
  }

  const showError = !!validationError && !loggingIn

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off" style={{ width: '100%' }}>
      <FormControl sx={{ width: '100%' }} className="xl">
        {showError && (
          <Stack direction="row" gap={1} mb={2}>
            <ErrorIcon fontSize="small" color="error" />
            {validationError.status === 401 || validationError.message === 'CredentialsSignin' ? (
              <div>
                <Typography color="error" data-testid="loginForm-failedMessage">
                  {translate('form.login.failedMessage')}
                </Typography>
                <Typography color="error">{translate('form.login.failedMessageContact')}</Typography>
              </div>
            ) : (
              <Typography color="error">{translate('form.login.failedMessageOther')}</Typography>
            )}
          </Stack>
        )}

        <Stack spacing={2}>
          <Box>
            <StyledLabel htmlFor="email">{translate('form.input.username')}</StyledLabel>
            <ControlledInput
              data-testid="loginForm-usernameInput"
              name="email"
              placeholder={translate('form.input.username')?.toString()}
              control={control}
              required
              error={showError}
              validationRules={{ required: translate('form.validation.required')?.toString() }}
              fullWidth
              inputProps={{ autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false }}
            />
          </Box>

          <Box>
            <StyledLabel htmlFor="password">{translate('form.input.password')}</StyledLabel>
            <ControlledInput
              data-testid="loginForm-passwordInput"
              name="password"
              placeholder={translate('form.input.password')?.toString()}
              control={control}
              error={showError}
              required
              validationRules={{ required: translate('form.validation.required')?.toString() }}
              fullWidth
              type={showPassword ? 'text' : 'password'}
              inputProps={{ autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" onClick={toggleShowPassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>
          <Button
            variant="contained"
            type="submit"
            data-testid="loginForm-submitButton"
            disableElevation
            disabled={loggingIn}
            fullWidth
            size="large"
            color="primary"
            sx={{ borderRadius: '2px' }}
          >
            {translate('form.login.signIn')}
          </Button>
          {!employeeSignIn && (
            <>
              <Typography align="center" color="secondary">
                {translate('form.login.signUpMessage')}
              </Typography>

              <MuiLink component={Link} href="/forgottenPassword" textAlign="center">
                {translate('form.login.forgottenPassword')}
              </MuiLink>
            </>
          )}
        </Stack>
      </FormControl>
    </form>
  )
}
